
import { useState } from 'react';

interface EmailConfig {
  id: string;
  email: string;
  name: string;
  alertTypes: {
    critical: boolean;
    warning: boolean;
    info: boolean;
  };
  active: boolean;
}

export default function NotificacionesEmail() {
  const [emailConfigs, setEmailConfigs] = useState<EmailConfig[]>([
    {
      id: '1',
      email: 'admin@poolchem.com',
      name: 'Administrador',
      alertTypes: { critical: true, warning: true, info: false },
      active: true
    },
    {
      id: '2',
      email: 'tecnico@poolchem.com',
      name: 'Técnico Principal',
      alertTypes: { critical: true, warning: false, info: false },
      active: true
    }
  ]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState({ email: '', name: '' });
  const [newAlertTypes, setNewAlertTypes] = useState({ critical: true, warning: true, info: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.email || !newEmail.name) return;

    setIsSubmitting(true);
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', newEmail.email);
      formData.append('name', newEmail.name);
      formData.append('alertTypes', JSON.stringify(newAlertTypes));
      formData.append('timestamp', new Date().toISOString());

      const response = await fetch('https://readdy.ai/api/form/d61ljd8omoleh5thvmdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        const newConfig: EmailConfig = {
          id: Date.now().toString(),
          email: newEmail.email,
          name: newEmail.name,
          alertTypes: newAlertTypes,
          active: true
        };
        setEmailConfigs(prev => [...prev, newConfig]);
        setNewEmail({ email: '', name: '' });
        setNewAlertTypes({ critical: true, warning: true, info: false });
        setSubmitStatus('success');
        setTimeout(() => {
          setShowAddModal(false);
          setSubmitStatus('idle');
        }, 1500);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleEmailActive = (id: string) => {
    setEmailConfigs(prev => prev.map(config =>
      config.id === id ? { ...config, active: !config.active } : config
    ));
  };

  const toggleAlertType = (id: string, type: 'critical' | 'warning' | 'info') => {
    setEmailConfigs(prev => prev.map(config =>
      config.id === id ? {
        ...config,
        alertTypes: { ...config.alertTypes, [type]: !config.alertTypes[type] }
      } : config
    ));
  };

  const removeEmail = (id: string) => {
    setEmailConfigs(prev => prev.filter(config => config.id !== id));
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center">
            <i className="ri-mail-send-line text-white text-2xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Notificaciones por Email</h3>
            <p className="text-sm text-slate-500">Configura alertas automáticas por correo</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
        >
          <i className="ri-add-line"></i>
          <span>Agregar Email</span>
        </button>
      </div>

      {/* Lista de Emails Configurados */}
      <div className="space-y-4">
        {emailConfigs.map((config) => (
          <div
            key={config.id}
            className={`p-4 rounded-xl border-2 transition-all ${
              config.active ? 'border-teal-200 bg-teal-50/50' : 'border-gray-200 bg-gray-50 opacity-60'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  config.active ? 'bg-teal-500' : 'bg-gray-400'
                }`}>
                  <i className="ri-user-line text-white"></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{config.name}</p>
                  <p className="text-sm text-slate-500">{config.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => toggleEmailActive(config.id)}
                  className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                    config.active ? 'bg-teal-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    config.active ? 'left-7' : 'left-1'
                  }`}></span>
                </button>
                <button
                  onClick={() => removeEmail(config.id)}
                  className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-slate-600 mr-2">Recibir alertas:</span>
              <button
                onClick={() => toggleAlertType(config.id, 'critical')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  config.alertTypes.critical
                    ? 'bg-red-500 text-white'
                    : 'bg-red-100 text-red-600'
                }`}
              >
                <i className="ri-alarm-warning-fill mr-1"></i>
                Críticas
              </button>
              <button
                onClick={() => toggleAlertType(config.id, 'warning')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  config.alertTypes.warning
                    ? 'bg-amber-500 text-white'
                    : 'bg-amber-100 text-amber-600'
                }`}
              >
                <i className="ri-alert-fill mr-1"></i>
                Advertencias
              </button>
              <button
                onClick={() => toggleAlertType(config.id, 'info')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  config.alertTypes.info
                    ? 'bg-teal-500 text-white'
                    : 'bg-teal-100 text-teal-600'
                }`}
              >
                <i className="ri-information-fill mr-1"></i>
                Informativas
              </button>
            </div>
          </div>
        ))}

        {emailConfigs.length === 0 && (
          <div className="text-center py-8">
            <i className="ri-mail-line text-4xl text-gray-300 mb-3"></i>
            <p className="text-gray-500">No hay emails configurados</p>
            <p className="text-sm text-gray-400">Agrega un email para recibir notificaciones</p>
          </div>
        )}
      </div>

      {/* Información de Funcionamiento */}
      <div className="mt-6 p-4 bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl border border-teal-200">
        <div className="flex items-start space-x-3">
          <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
          <div>
            <p className="text-sm font-medium text-teal-800">¿Cómo funciona?</p>
            <p className="text-xs text-teal-600 mt-1">
              Cuando se detecte un valor fuera de rango, se enviará automáticamente un email 
              a los destinatarios configurados según el tipo de alerta seleccionado. Las alertas 
              críticas se envían inmediatamente, las advertencias cada 15 minutos y las informativas 
              en un resumen diario.
            </p>
          </div>
        </div>
      </div>

      {/* Modal Agregar Email */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAddModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-800">Agregar Notificación</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form id="email-notification-form" data-readdy-form onSubmit={handleAddEmail}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre</label>
                  <input
                    type="text"
                    name="name"
                    value={newEmail.name}
                    onChange={(e) => setNewEmail(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ej: Técnico Principal"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={newEmail.email}
                    onChange={(e) => setNewEmail(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="correo@ejemplo.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Tipos de Alerta</label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newAlertTypes.critical}
                        onChange={(e) => setNewAlertTypes(prev => ({ ...prev, critical: e.target.checked }))}
                        className="w-5 h-5 text-red-500 rounded focus:ring-red-500"
                      />
                      <span className="flex items-center space-x-2">
                        <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                        <span className="text-sm text-slate-700">Alertas Críticas</span>
                      </span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newAlertTypes.warning}
                        onChange={(e) => setNewAlertTypes(prev => ({ ...prev, warning: e.target.checked }))}
                        className="w-5 h-5 text-amber-500 rounded focus:ring-amber-500"
                      />
                      <span className="flex items-center space-x-2">
                        <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                        <span className="text-sm text-slate-700">Advertencias</span>
                      </span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newAlertTypes.info}
                        onChange={(e) => setNewAlertTypes(prev => ({ ...prev, info: e.target.checked }))}
                        className="w-5 h-5 text-teal-500 rounded focus:ring-teal-500"
                      />
                      <span className="flex items-center space-x-2">
                        <span className="w-3 h-3 bg-teal-500 rounded-full"></span>
                        <span className="text-sm text-slate-700">Informativas</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-lg flex items-center space-x-2">
                  <i className="ri-checkbox-circle-fill"></i>
                  <span>Email agregado correctamente</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center space-x-2">
                  <i className="ri-error-warning-fill"></i>
                  <span>Error al agregar email. Intenta de nuevo.</span>
                </div>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line"></i>
                      <span>Guardar</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
